import type { Buffer } from "../model/buffer";
import { isBlankLine } from "../model/buffer";
import type { Cursor } from "../model/cursor";
import type { Range } from "../model/range";
import { classAt, stepBackward, stepForward } from "./word-class";

function wordForwardOnce(buffer: Buffer, pos: Cursor): Cursor {
  let cur = pos;
  const startClass = classAt(buffer, cur);

  if (startClass !== "blank") {
    let next = stepForward(buffer, cur);
    while (next !== null && classAt(buffer, next) === startClass) {
      cur = next;
      next = stepForward(buffer, cur);
    }
    if (next === null) {
      return cur;
    }
    cur = next;
  } else {
    const next = stepForward(buffer, cur);
    if (next === null) {
      return cur;
    }
    cur = next;
  }

  while (true) {
    if (cur.col === 0 && isBlankLine(buffer, cur.line)) {
      return cur;
    }
    if (classAt(buffer, cur) !== "blank") {
      return cur;
    }
    const next = stepForward(buffer, cur);
    if (next === null) {
      return cur;
    }
    cur = next;
  }
}

function wordBackwardOnce(buffer: Buffer, pos: Cursor): Cursor {
  let cur = stepBackward(buffer, pos);
  if (cur === null) {
    return pos;
  }

  while (true) {
    if (cur.col === 0 && isBlankLine(buffer, cur.line)) {
      return cur;
    }
    if (classAt(buffer, cur) !== "blank") {
      break;
    }
    const prev = stepBackward(buffer, cur);
    if (prev === null) {
      return cur;
    }
    cur = prev;
  }

  const runClass = classAt(buffer, cur);
  while (true) {
    const prev = stepBackward(buffer, cur);
    if (prev === null || classAt(buffer, prev) !== runClass) {
      return cur;
    }
    cur = prev;
  }
}

function wordEndForwardOnce(buffer: Buffer, pos: Cursor): Cursor {
  let cur = stepForward(buffer, pos);
  if (cur === null) {
    return pos;
  }

  while (classAt(buffer, cur) === "blank") {
    const next = stepForward(buffer, cur);
    if (next === null) {
      return cur;
    }
    cur = next;
  }

  const runClass = classAt(buffer, cur);
  while (true) {
    const next = stepForward(buffer, cur);
    if (next === null || classAt(buffer, next) !== runClass) {
      return cur;
    }
    cur = next;
  }
}

function repeat(
  fn: (buffer: Buffer, pos: Cursor) => Cursor,
  buffer: Buffer,
  cursor: Cursor,
  count: number | null,
): Cursor {
  let cur = cursor;
  const times = Math.max(count ?? 1, 1);
  for (let i = 0; i < times; i += 1) {
    cur = fn(buffer, cur);
  }
  return cur;
}

/** `w`: 次の単語の先頭へ移動する。exclusive。 */
export function wordForward(buffer: Buffer, cursor: Cursor, count: number | null): Range {
  const end = repeat(wordForwardOnce, buffer, cursor, count);
  return { start: cursor, end, kind: "exclusive" };
}

/** `b`: 前の単語の先頭へ移動する。exclusive。 */
export function wordBackward(buffer: Buffer, cursor: Cursor, count: number | null): Range {
  const end = repeat(wordBackwardOnce, buffer, cursor, count);
  return { start: cursor, end, kind: "exclusive" };
}

/** `e`: 単語の末尾へ移動する。inclusive（`w` との1文字差の要点）。 */
export function wordEndForward(buffer: Buffer, cursor: Cursor, count: number | null): Range {
  const end = repeat(wordEndForwardOnce, buffer, cursor, count);
  return { start: cursor, end, kind: "inclusive" };
}
